import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Define interfaces for the API response
export interface E53StratumData {
  stratumId: number;
  datasetId: number;
  stratumStatus: string | null;
  oeFromCode: string;
  oeToCode: string;
  mailCategory: string;
  formatCorrected: string;
  netOrGrossAccounting: string;
  receptacleCategory: string;
  receivedWeightStratum: string;
  sampledWeightStratum: string;
  effectiveSamplingRateStratum: string;
  receivedReceptacleStratum: string;
}

export interface E53StratumResponse {
  content: E53StratumData[];
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  last: boolean;
  totalPages: number;
  totalElements: number;
  first: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  empty: boolean;
}

interface UseE53StratumDataParams {
  fileId: number | string;
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'ASC' | 'DESC';
  enabled?: boolean;
}

/**
 * Hook to fetch e53 stratum data for a specific file ID
 * 
 * @param params Object containing file ID, pagination parameters, and enabled flag
 * @returns Query result with the e53 stratum data
 */
export const useE53StratumData = ({ 
  fileId, 
  page = 0, 
  size = 20, 
  sort = 'stratumId',
  direction = 'ASC',
  enabled = true 
  }: UseE53StratumDataParams) => {
  const numericFileId = typeof fileId === 'string' ? parseInt(fileId, 10) : fileId;
  const isValidId = !isNaN(numericFileId) && numericFileId > 0;

  return useQuery<E53StratumResponse>({
    queryKey: ['e53StratumData', numericFileId, page, size, sort, direction],
    queryFn: async () => {
      if (!isValidId) {
        throw new Error('Invalid file ID');
      }

      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('size', String(size));
      
      // Add sorting parameters if provided
      if (sort) {
        // Format for backend sorting - typically "fieldName,direction"
        params.append('sort', `${sort},${direction}`);
      }

      const response = await axios.get(`/ipc/stratum/e53/${numericFileId}?${params.toString()}`);
      return response.data;
    },
    staleTime: 0, // Set to 0 to ensure fresh data on pagination changes
    gcTime: 5 * 60 * 1000, // Cache for 5 minutes, but will always refetch
    enabled: enabled && isValidId,
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });
}; 